import * as React from "react";
import {
  useDrag,
  useDrop
} from "react-dnd";
import type {
  XYCoord,
  Identifier
} from "dnd-core";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import { Icon } from "src/library/components/Icon";
import { ActionButton } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import { IPromotionalTag } from "src/modules/promotions/api/promotional-tags.api";

import PromotionalTagColorLabel from "./PromotionalTagColorLabel";

export interface PromotionTagDraggableCardProps extends IPromotionalTag {
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  onDeletePromotionTag: (promotionTag: IPromotionalTag) => void;
  priorityChanged: boolean;
  updatePriorityChanged: (priorityChanged: boolean) => void;
}

export const ItemTypes = { CARD: "card" };

interface DragItem {
  index: number;
  id: string;
  type: string;
}

/**
 * Dragable parent Promotion Tag view
 *
 * @returns a Promotion Tag row react element
 */
export const PromotionTagDraggableCard: React.ReactFCWithChildren<PromotionTagDraggableCardProps> = ({
  id,
  label,
  color,
  priority,
  moveCard,
  onDeletePromotionTag,
  priorityChanged,
  updatePriorityChanged
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();

  const [ { handlerId }, drop ] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.CARD,
    collect(monitor) {
      // let consumer component know the order has changed
      if (monitor.didDrop() && !priorityChanged) {
        updatePriorityChanged(true);
      }

      return { handlerId: monitor.getHandlerId() };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = priority;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });

  const [
    { isDragging },
    drag,
    dragPreview
  ] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return {
        id,
        index: priority
      };
    },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));
  dragPreview(drop(previewRef));

  return (
    <PromotionTagDraggableCardWrapper>
      <div
        className="parent-div"
        ref={previewRef}
        data-handler-id={handlerId}
        style={{ opacity }}
      >
        <div
          ref={ref}
          className="drag-icon-wrapper"
        >
          <Icon
            icon={Icons.DRAG}
            size={15}
          />
        </div>

        <div className="draggable-tag">
          <div className="tag-details">
            <Text>
              {label}
            </Text>

            <PromotionalTagColorLabel color={color} />
          </div>

          <div>
            <ActionButton
              title="Actions"
              items={[
                {
                  title: "Edit Tag",
                  action: () => {
                    history.push(`/admin/promotions/promotional-tags/${id}`);
                  }
                },
                {
                  title: "Delete Tag",
                  action: () => {
                    onDeletePromotionTag({
                      id,
                      label,
                      color,
                      priority
                    });
                  }
                }
              ]}
            />
          </div>
        </div>

      </div>

    </PromotionTagDraggableCardWrapper>
  );
};

const PromotionTagDraggableCardWrapper = styled.div`
  ${({ theme }) => css`
    .drag-icon-wrapper {          
      svg {
        margin-right: ${theme.spacing.margins.md};

        :hover {
          cursor: grab;
        }
      }
    }

    .parent-div {
      display: flex;
      width: 100%;
      align-items: center;

      .icon-wrapper {
        width: 30px;

        svg:hover {
          cursor: pointer;
        }
      }
    }

    .draggable-tag {
      width: 100%;
      background: ${theme.colors.white};
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md}; 
      border-bottom: ${theme.colors.lightGrey} 1px solid;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .tag-details {
        display: flex;
        align-items: center;
      }
    }
  `}
`;
